import type { ApplicationSpend } from './spend.js'
import type { RepositoryActivity } from './repository.js'
import type { SleepUnit } from './traffic.js'

export type { ApplicationSpend, RepositoryActivity }

export const HEALTH_STATES = [
  'operational',
  'degraded',
  'outage',
  'maintenance',
  'unknown',
] as const

export type HealthState = (typeof HEALTH_STATES)[number]
export type Severity = 'info' | 'warning' | 'critical'
export type DomainSource = 'configured' | 'caddy' | 'netlify' | 'spaceship'
export type TechnologySource = DomainSource | 'vps' | 'detected'
export type TechnologyCategory =
  | 'language'
  | 'framework'
  | 'runtime'
  | 'database'
  | 'hosting'
  | 'dns'
  | 'registrar'
  | 'edge'
  | 'build'
  | 'service'
export type TechnologyConfidence = 'declared' | 'high' | 'medium' | 'low'

export interface TechnologySignal {
  name: string
  category: TechnologyCategory
  sources: TechnologySource[]
  confidence: TechnologyConfidence
  evidence?: string
}

export interface DeploymentTimestamp {
  at: string
  sources: TechnologySource[]
  confidence: TechnologyConfidence
  evidence?: string
}

export interface ApplicationDeploymentTimeline {
  first?: DeploymentTimestamp
  latest?: DeploymentTimestamp
}

export interface DomainInventoryEntry {
  hostname: string
  sources: DomainSource[]
  role?: 'primary' | 'alias' | 'platform' | 'registration'
}

export interface ApplicationIdentity {
  id: string
  name: string
  description: string
  environment: string
  owner: string
  kind?: 'application' | 'infrastructure' | 'domain'
  homepage?: string
  /** Curated logo/favicon override. Relay otherwise discovers the homepage icon. */
  iconUrl?: string
  iconFit?: 'contain' | 'cover'
  iconPosition?: 'left' | 'center' | 'right'
  repository?: string
  version?: string
  release?: string
  commit?: string
  region?: string
  tags?: string[]
  domains?: string[]
  domainInventory?: DomainInventoryEntry[]
  technologies?: TechnologySignal[]
  deployment?: ApplicationDeploymentTimeline
}

export interface HealthComponent {
  id: string
  name: string
  description?: string
  type: string
  status: HealthState
  required: boolean
  observedAt: string
  latencyMs?: number
  message?: string
  lastSuccessAt?: string
  lastFailureAt?: string
  /**
   * True when this component's status is a roll-up of the ones beneath it.
   * A roll-up is the right thing to show on a card and the wrong thing to put
   * in a notification: "Model index degraded" is a restatement of its own
   * children, where the child itself knows it was last verified six days ago.
   */
  aggregate?: boolean
  /**
   * True when the failure is "no answer" rather than a bad answer — a timeout,
   * a refused connection, a reset socket. Evidence about the moment, not about
   * the service, so it has to be seen twice before it becomes an incident.
   */
  transient?: boolean
  details?: Record<string, string | number | boolean | null>
}

export interface HealthIncident {
  id: string
  applicationId: string
  componentId?: string
  title: string
  status: 'investigating' | 'identified' | 'monitoring' | 'resolved'
  severity: Severity
  startedAt: string
  updatedAt: string
  resolvedAt?: string
  /** When a warning first became critical. Set once, so a flap cannot re-page. */
  escalatedAt?: string
  message?: string
}

export const PRODUCT_METRIC_CATEGORIES = [
  'users',
  'engagement',
  'content',
  'growth',
  'revenue',
] as const

export const PRODUCT_METRIC_SEMANTICS = [
  'total-users',
  'new-users',
  'active-users',
  'online-users',
  'authenticated-active-users',
  'page-views',
  'visits',
  'unique-visitors',
  'requests',
  'core-action',
  'conversion',
  'paying-customers',
  'active-subscriptions',
  'payments',
  'refunds',
  'revenue',
  'recurring-revenue',
  'custom',
] as const

export const PRODUCT_METRIC_PERIODS = [
  'instant',
  '24h',
  '7d',
  '30d',
  'all-time',
] as const

export type ProductMetricCategory = (typeof PRODUCT_METRIC_CATEGORIES)[number]
export type ProductMetricSemantic = (typeof PRODUCT_METRIC_SEMANTICS)[number]
export type ProductMetricPeriod = (typeof PRODUCT_METRIC_PERIODS)[number]

export const PRODUCT_STATS_EXCLUDED_ACTORS = [
  'owner', 'team', 'test', 'monitor', 'bot', 'agent',
] as const

/** A producer declaration, not independent proof that every actor was classified. */
export interface ProductStatsAudience {
  policyVersion: '1.0'
  scope: 'external-only' | 'mixed' | 'unknown'
  excludedActors: Array<(typeof PRODUCT_STATS_EXCLUDED_ACTORS)[number]>
  method: string
  /** Earliest event time for which this exclusion policy has complete coverage. */
  coverageStart: string
}

/** A bounded, aggregate product fact. Product metrics must never contain PII. */
export interface ProductMetric {
  id: string
  name: string
  description?: string
  category: ProductMetricCategory
  semantic: ProductMetricSemantic
  status: 'available' | 'suppressed' | 'unavailable' | 'not-applicable'
  value?: number
  unit: 'count' | 'percent' | 'currency-minor' | 'seconds'
  period: ProductMetricPeriod
  aggregation: 'gauge' | 'cumulative'
  /** ISO 4217 code. Never combine money values whose currencies are absent or differ. */
  currency?: string
  /** Exact half-open interval [start, end), in UTC, for a rolling measurement. */
  window?: { start: string; end: string }
}

export interface ProductStatsReport {
  schemaVersion: '1.0'
  observedAt: string
  applicationId: string
  audience?: ProductStatsAudience
  metrics: ProductMetric[]
}

export interface ProductMetricChange {
  absolute: number
  percent: number | null
  baselineAt: string
}

export interface ProductMetricSnapshot extends ProductMetric {
  change24h?: ProductMetricChange
  change7d?: ProductMetricChange
}

export interface ApplicationProductMetrics {
  sourceStatus: 'fresh' | 'stale' | 'invalid' | 'unavailable'
  observedAt?: string
  /** Derived by Relay from the observation time and the configured source freshness. */
  expiresAt?: string
  lastSuccessAt?: string
  message?: string
  audience?: ProductStatsAudience
  metrics: ProductMetricSnapshot[]
}

export interface StandardHealthReport {
  schemaVersion: '1.0'
  observedAt: string
  application: ApplicationIdentity
  status: HealthState
  summary: string
  uptimeSeconds?: number
  responseTimeMs?: number
  components: HealthComponent[]
  incidents?: HealthIncident[]
  links?: {
    dashboard?: string
    runbook?: string
    support?: string
    stats?: string
  }
}

export type AssertionOperator =
  | 'equals'
  | 'notEquals'
  | 'exists'
  | 'truthy'
  | 'lessThan'
  | 'greaterThan'
  | 'notOlderThanSeconds'

export interface JsonAssertion {
  path: string
  operator: AssertionOperator
  value?: string | number | boolean
}

export interface DerivedComponentConfig {
  id: string
  name: string
  description?: string
  path: string
  operator: AssertionOperator
  value?: string | number | boolean
  required?: boolean
  healthyMessage?: string
  unhealthyMessage?: string
}

interface BaseProbeConfig {
  id: string
  name: string
  description?: string
  required?: boolean
  timeoutMs?: number
}

export interface HttpProbeConfig extends BaseProbeConfig {
  kind: 'http' | 'json' | 'standard'
  url: string
  method?: 'GET' | 'HEAD'
  acceptedStatuses?: number[]
  expect?: JsonAssertion[]
  components?: DerivedComponentConfig[]
}

export interface TlsProbeConfig extends BaseProbeConfig {
  kind: 'tls'
  hostname: string
  port?: number
  warnDays?: number
  criticalDays?: number
}

export interface DnsProbeConfig extends BaseProbeConfig {
  kind: 'dns'
  hostname: string
  recordType?: 'address' | 'nameserver'
  expectedAddresses?: string[]
  expectedNameservers?: string[]
}

export interface TcpProbeConfig extends BaseProbeConfig {
  kind: 'tcp'
  hostname: string
  port: number
}

export interface ProviderProbeConfig extends BaseProbeConfig {
  kind: 'provider'
  provider: 'netlify' | 'spaceship'
  resourceType: 'site' | 'registration'
  status: HealthState
  message: string
  details?: Record<string, string | number | boolean | null>
}

export interface HostThresholds {
  cpuWarningPercent: number
  cpuCriticalPercent: number
  memoryWarningPercent: number
  memoryCriticalPercent: number
  swapWarningPercent: number
  swapCriticalPercent: number
  /** Occupancy that warns of low reserve even without corroborated memory pressure. */
  swapExhaustionPercent: number
  /** Available-RAM percentage at or below which swap occupancy becomes meaningful. */
  swapMemoryHeadroomPercent: number
  diskWarningPercent: number
  diskCriticalPercent: number
  inodeWarningPercent: number
  inodeCriticalPercent: number
  loadWarningPerCore: number
  loadCriticalPerCore: number
  pressureWarningPercent: number
  pressureCriticalPercent: number
}

export interface HostProbeConfig extends BaseProbeConfig {
  kind: 'host'
  path: string
  freshnessMs?: number
  thresholds?: Partial<HostThresholds>
}

export type ProbeConfig =
  | HttpProbeConfig
  | TlsProbeConfig
  | DnsProbeConfig
  | TcpProbeConfig
  | ProviderProbeConfig
  | HostProbeConfig

export interface ApplicationConfig extends ApplicationIdentity {
  accent?: string
  /** Honest, configuration-owned inventory for static products without a server telemetry surface. */
  declaredProductMetrics?: ProductMetric[]
  showcase?: {
    published: boolean
    order?: number
    /** Explicit allowlist. Product metrics are private unless listed here. */
    publicMetricIds?: string[]
    /**
     * Why a renderable product is kept off the public build log.
     *
     * Publishing is the default for anything with a public homepage and a
     * screenshot, so an unpublished one is an exception, and an exception
     * nobody wrote down is indistinguishable from an oversight — which is how
     * eleven finished products sat unlisted for months.
     */
    withheldReason?: string
  }
  stats?: {
    url: string
    tokenEnv?: string
    freshnessSeconds?: number
  }
  /**
   * Metered paid-provider consumption. Separate from `stats` because the two
   * answer different questions and fail independently: product telemetry can
   * be down while spend reporting is the thing you most need to still read.
   */
  spend?: {
    url: string
    tokenEnv?: string
    freshnessSeconds?: number
  }
  probes: ProbeConfig[]
  previews?: PreviewConfig[]
}

export interface PreviewConfig {
  id: string
  name: string
  description?: string
  url: string
  width?: number
  height?: number
  timeoutMs?: number
  settleMs?: number
}

export interface PreviewFrame {
  id: string
  capturedAt: string
  imageUrl: string
  width: number
  height: number
  pageTitle?: string
  httpStatus?: number
}

export interface ApplicationPreview {
  id: string
  name: string
  description?: string
  url: string
  status: HealthState
  observedAt: string
  latencyMs?: number
  message: string
  frames: PreviewFrame[]
}

export interface MonitorConfig {
  schemaVersion: '1.0'
  title: string
  subtitle: string
  applications: ApplicationConfig[]
  inventorySources?: InventorySourceStatus[]
  /** Derived from Caddy routes and the VPS container inventory. */
  sleepUnits?: SleepUnit[]
}

export interface InventorySourceStatus {
  source: Exclude<DomainSource, 'configured'> | 'vps'
  status: HealthState
  discovered: number
  message: string
  observedAt: string
  stale?: boolean
}

export interface VpsDeploymentContainer {
  name: string
  project?: string
  service?: string
  image: string
  imageId: string
  createdAt: string
}

export interface VpsStaticRelease {
  source: string
  destination: string
  modifiedAt: string
}

export interface VpsDeploymentInventory {
  schemaVersion: '1.0'
  observedAt: string
  containers: VpsDeploymentContainer[]
  staticReleases: VpsStaticRelease[]
}

export interface ApplicationSnapshot {
  application: ApplicationIdentity & { accent?: string }
  status: HealthState
  summary: string
  observedAt: string
  durationMs: number
  availability24h: number | null
  components: HealthComponent[]
  incidents: HealthIncident[]
  previews: ApplicationPreview[]
  productMetrics?: ApplicationProductMetrics
  spend?: ApplicationSpend
  /** Source-repository commit activity, refreshed hourly. */
  repository?: RepositoryActivity
  /** Stopped by the sleeper while idle. Not probed until it wakes. */
  asleep?: boolean
}

export interface FleetSummary {
  status: HealthState
  observedAt: string
  durationMs: number
  applications: number
  operational: number
  degraded: number
  outage: number
  unknown: number
  activeIncidents: number
  checks: number
  p95LatencyMs: number | null
  availability24h: number | null
}

export interface HistoryPoint {
  observedAt: string
  status: HealthState
  durationMs: number
  /** Deliberately stopped; excluded from availability. */
  asleep?: true
}

export interface ProductMetricHistoryPoint {
  observedAt: string
  value: number
}

export type ProductMetricHistory = Record<
  string,
  Record<string, ProductMetricHistoryPoint[]>
>

export interface HostPressure {
  cpuPercent: number
  memoryPercent: number
  ioPercent: number
  /**
   * Time in which *every* runnable task was stalled, not merely one of them.
   * Optional because telemetry recorded before these were captured is still
   * replayed from history.
   */
  memoryFullPercent?: number
  ioFullPercent?: number
}

export interface HostMachineFacts {
  hostname: string
  operatingSystem: string
  kernel: string
  architecture: string
  cpuModel: string
  cpuCores: number
  totalMemoryBytes: number
  totalSwapBytes: number
  diskPath: string
  diskTotalBytes: number
  diskTotalInodes: number
  bootedAt: string
}

export interface HostTelemetryPoint {
  observedAt: string
  status: HealthState
  cpuPercent: number
  cpuAverage1mPercent: number
  load1: number
  load5: number
  load15: number
  memoryUsedPercent: number
  memoryUsedBytes: number
  memoryAvailableBytes: number
  swapUsedPercent: number
  swapUsedBytes: number
  diskUsedPercent: number
  diskUsedBytes: number
  diskFreeBytes: number
  inodeUsedPercent: number
  networkRxBytes: number
  networkTxBytes: number
  networkRxBytesPerSecond: number
  networkTxBytesPerSecond: number
  diskReadBytesPerSecond: number
  diskWriteBytesPerSecond: number
  processCount: number
  pressure: HostPressure
}

export interface HostTelemetrySample extends HostTelemetryPoint {
  schemaVersion: '1.0'
  machine: HostMachineFacts
  collectedAtMonotonicMs: number
}

export interface InfrastructureEvent {
  id: string
  occurredAt: string
  endedAt?: string
  type: 'incident' | 'recovery' | 'resource'
  severity: Severity
  title: string
  detail: string
  applicationId?: string
  incidentId?: string
}

export interface InfrastructureInsight {
  id: string
  severity: 'positive' | 'info' | 'warning' | 'critical'
  title: string
  detail: string
  scope?: 'now' | 'recent' | 'trend' | 'forecast' | 'baseline'
  evidence?: string
  recommendation?: string
}

export interface InfrastructureSnapshot {
  schemaVersion: '1.0'
  status: HealthState
  observedAt?: string
  stale: boolean
  collectionIntervalMs: number
  retentionDays: number
  range: '1h' | '6h' | '24h' | '7d' | '30d'
  machine?: HostMachineFacts
  current?: HostTelemetryPoint
  thresholds: HostThresholds
  componentStatuses?: Record<string, HealthState>
  history: HostTelemetryPoint[]
  events: InfrastructureEvent[]
  insights: InfrastructureInsight[]
}

export interface DashboardSnapshot {
  schemaVersion: '1.0'
  requestId: string
  product: {
    title: string
    subtitle: string
  }
  reason: 'startup' | 'scheduled' | 'visit' | 'manual' | 'cached'
  fresh: boolean
  cacheAgeMs: number
  nextScheduledCheckAt: string
  summary: FleetSummary
  applications: ApplicationSnapshot[]
  history: Record<string, HistoryPoint[]>
  productMetricHistory: ProductMetricHistory
  incidents: HealthIncident[]
  notifications: {
    enabled: boolean
    lastDeliveryAt?: string
    lastError?: string
    /**
     * Per-incident reminder state, keyed by incident id. Held per incident
     * rather than globally so a long-running degradation cannot mask a second,
     * newer failure by monopolising the one reminder slot.
     */
    reminders?: Record<string, { sentAt: string; count: number }>
  }
}

export interface PublicShowcaseTechnology {
  name: string
  category: TechnologyCategory
  confidence: TechnologyConfidence
}

export interface PublicShowcaseScreenshot {
  imageUrl: string
  capturedAt: string
  width: number
  height: number
  pageTitle?: string
}

export interface PublicShowcasePreview {
  id: string
  name: string
  url: string
  status: HealthState
  screenshot: PublicShowcaseScreenshot
}

export interface PublicShowcaseDomain {
  hostname: string
  url: string
  primary: boolean
}

export interface PublicShowcaseApplication {
  id: string
  name: string
  description: string
  homepage: string
  iconUrl?: string
  iconFit?: 'contain' | 'cover'
  iconPosition?: 'left' | 'center' | 'right'
  status: HealthState
  observedAt: string
  latestDeploymentAt?: string
  domains: PublicShowcaseDomain[]
  technologies: PublicShowcaseTechnology[]
  previews: PublicShowcasePreview[]
  productMetrics: ProductMetricSnapshot[]
  /** Public slice of repository activity. Never includes the repo URL itself. */
  activity?: PublicShowcaseActivity
  accent?: string
}

export interface PublicShowcaseActivity {
  commits30d: number
  commits7d?: number
  totalCommits?: number
  lastCommitAt?: string
  /** Commits per day for the trailing 30 days, oldest first. */
  daily: number[]
}

export interface PublicShowcaseSnapshot {
  schemaVersion: '1.0'
  title: string
  description: string
  observedAt: string
  applications: PublicShowcaseApplication[]
}
